import React, {forwardRef, useImperativeHandle, useState} from 'react';
import type {IDidomiObject} from '@didomi/react';
import {DidomiSDK} from '@didomi/react';
import config from '@deezer/next/services/config';
import {cdp} from '@deezer/next/services/tracking/LogCenter';

enum DidomiPurposes {
	AdDelivery = 'ad_delivery',
	AdvertisingPersonalization = 'advertising_personalization',
	Analytics = 'analytics',
	ContentPersonalization = 'content_personalization',
	Cookies = 'cookies',
	CookiesAnalytics = 'cookies_analytics',
	CookiesMarketing = 'cookies_marketing',
	CookiesSocial = 'cookies_social',
	CreateAdsProfile = 'create_ads_profile',
	CreateContentProfile = 'create_content_profile',
	DeviceAccess = 'device_access',
	ImproveProducts = 'improve_products',
	LinkDevices = 'link_devices',
	MarketResearch = 'market_research',
	MeasureAdPerformance = 'measure_ad_performance',
	MeasureContentPerformance = 'measure_content_performance',
	OfflineMatch = 'offline_match',
	PreciseGeo = 'precise_geo',
	SelectBasicAds = 'select_basic_ads',
	SelectPersonalizedAds = 'select_personalized_ads',
	SelectPersonalizedContent = 'select_personalized_content',
	UseLimitedDataToSelectContent = 'use_limited_data_to_select_content',
}

const AnalyticPurposes = [
	DidomiPurposes.MeasureContentPerformance,
	DidomiPurposes.ImproveProducts,
	DidomiPurposes.MarketResearch,
];

enum DidomiVendors {
	AMPLITUDE = 'c:amplitude',
	SEGMENT = 'c:segment',
}

const DidomiConsent = forwardRef<IDidomiObject | undefined>((props, ref) => {
	const [didomiSDK, setDidomiSDK] = useState<IDidomiObject>();

	useImperativeHandle(ref, () => didomiSDK, [didomiSDK]);

	const handleReady = (didomi: IDidomiObject) => {
		setDidomiSDK(didomi);

		document.body.setAttribute('didomi-window-ready', 'true');

		const userStatus = didomi.getCurrentUserStatus();

		const amplitudeVendorStatus =
			userStatus?.vendors?.['amplitude-d4c']?.enabled;

		const segmentVendorStatus = userStatus?.vendors?.['segment-d4c']?.enabled;

		const statistics =
			amplitudeVendorStatus &&
			segmentVendorStatus &&
			AnalyticPurposes.every(
				purpose => userStatus?.purposes?.[purpose]?.enabled,
			);

		cdp.setConsent({
			consentString: '',
			statistics: statistics ? 1 : 0,
			targetedAdvertising: 0,
		});
	};

	const handleConsentChanged = (cwtToken: {
		consents: {
			purpose: DidomiPurposes;
			vendors: {
				id: string;
				status: boolean;
			}[];
		}[];
	}) => {
		const purposes = cwtToken.consents.reduce<{
			[key: string]: {
				[key: string]: boolean;
			};
		}>((acc, consent) => {
			acc[consent.purpose] = consent.vendors.reduce<{
				[key: string]: boolean;
			}>((acc1, vendor) => {
				acc1[vendor.id] = vendor.status;

				return acc1;
			}, {});

			return acc;
		}, {});

		const statistics = AnalyticPurposes.every(
			purpose =>
				purposes[purpose][DidomiVendors.SEGMENT] &&
				purposes[purpose][DidomiVendors.AMPLITUDE],
		);

		cdp.setConsent({
			consentString: '',
			statistics: statistics ? 1 : 0,
			targetedAdvertising: 0,
		});
	};

	return (
		<DidomiSDK
			apiKey={config.get('didomi_public_key')}
			noticeId="L8tda7LG"
			gdprAppliesGlobally={true}
			sdkPath="https://sdk.privacy-center.org/"
			embedTCFStub={true}
			onReady={handleReady}
			onConsentChanged={handleConsentChanged}
		/>
	);
});

DidomiConsent.displayName = 'DidomiConsent';

export default DidomiConsent;
